// Article « L'épreuve du plateau moto A1 / A2 — Guide complet »
// Pédagogie CODE 2 ROUES : explication complète des 6 épreuves officielles
// du plateau, focus sur l'escargot, fautes éliminatoires et conseils.
// Conforme au référentiel ETM / ECSR 2026.

import {
  Bike, Target, AlertTriangle, CheckCircle2, XCircle, Eye,
  Timer, ShieldCheck, Flag, Zap, Gauge, Award, Route,
  Lightbulb, Trophy, Compass,
} from 'lucide-react';

export const plateauArticle = {
  slug: 'epreuve-plateau-moto-a1-a2',
  weekVolume: null,
  weekYear: 2026,
  monthLabel: 'Guide permanent',
  category: 'Examen & épreuves',
  isGuide: true,
  shortTitle: 'Le plateau moto A1/A2',
  title: "L'épreuve du plateau moto A1 / A2",
  kicker: 'Guide complet — épreuve hors circulation',
  subtitle:
    "Escargot, slalom lent, freinage d'urgence, évitement… Tout ce qu'il faut maîtriser pour réussir l'épreuve du plateau du permis moto, avec les clés techniques de nos moniteurs ECSR.",
  heroImage:
    'https://images.unsplash.com/photo-1568772585407-9361f9bf3a87?w=2400&q=80&auto=format&fit=crop',
  heroImageAlt:
    'Motarde en formation sur une piste de moto-école avec cônes de parcours lent',
  topRightKicker: { line1: 'Guide ECSR', line2: 'Examen plateau' },
  readTimeMin: 8,
  seoDescription:
    "Réussir l'épreuve du plateau moto A1 / A2 : parcours lent (escargot), slalom rapide, freinage d'urgence, évitement, vérifications. Le guide complet ECSR, techniques et conseils.",
  seoKeywords:
    "plateau moto, permis A1, permis A2, escargot moto, freinage d'urgence moto, évitement moto, parcours lent, examen plateau, code 2 roues, moto-école",
  about:
    'Épreuves du permis moto, plateau hors circulation, technique de pilotage',
  source: {
    label: 'Référentiel ECSR — Permis A1 / A2',
    url: 'https://www.service-public.fr/particuliers/vosdroits/F2825',
    refs:
      'Arrêté du 19 février 2010 modifié — épreuve hors circulation (plateau) du permis moto.',
  },

  chapo: {
    kicker: 'Pourquoi cet article',
    body:
      "Le <strong>plateau moto</strong> est l'étape obligatoire <em>avant</em> l'épreuve de circulation. Réalisée sur <strong>piste fermée</strong>, elle prouve que tu maîtrises ta machine : équilibre à basse vitesse, gestes d'urgence, trajectoires précises. L'épreuve commence par <strong>des vérifications de l'équipement et de la moto</strong>, puis enchaîne <strong>5 épreuves de maniabilité</strong>. Cet article détaille chaque étape, les techniques, les fautes éliminatoires et les conseils de nos moniteurs pour te donner toutes les chances de réussir du premier coup.",
    sideStat: {
      kicker: 'À retenir',
      big: '5',
      body:
        "épreuves de maniabilité après les vérifications de début : <strong>déplacement sans moteur</strong>, <strong>parcours lent</strong> (dont escargot), <strong>parcours rapide</strong>, <strong>freinage d'urgence</strong> et <strong>évitement</strong>.",
      accent: '#cf2031',
    },
  },

  pullQuote: {
    text:
      "Ta moto va où tu regardes. Maîtriser le plateau, c'est d'abord maîtriser son regard, sa respiration et son embrayage.",
    source: 'Moniteurs ECSR — équipe pédagogique CODE 2 ROUES',
  },

  sections: [
    {
      part: 'Partie 1 — À quoi sert le plateau ?',
      title: 'Les objectifs de l\'épreuve',
      intro:
        "L'examinateur évalue ta capacité à <strong>manipuler la moto en sécurité</strong>, à <strong>gérer l'équilibre à basse vitesse</strong>, à <strong>réagir en urgence</strong> et à <strong>respecter des trajectoires précises</strong>. C'est l'épreuve qui prouve que tu as intégré les fondamentaux du pilotage.",
      cards: [
        {
          icon: ShieldCheck,
          accent: '#16a34a',
          kicker: 'Objectif 1',
          title: 'Manipuler en sécurité',
          body:
            'Déplacer, stationner, pousser ta moto sans la faire tomber. Un motard qui ne sait pas manipuler sa machine à pied est un danger pour lui-même et les autres.',
        },
        {
          icon: Compass,
          accent: '#1d4ed8',
          kicker: 'Objectif 2',
          title: "Équilibre à basse vitesse",
          body:
            "La basse vitesse est <strong>l'ennemi de l'équilibre</strong>. Savoir rouler lentement sans poser le pied, c'est savoir gérer l'embrayage, le frein arrière et le regard.",
        },
        {
          icon: AlertTriangle,
          accent: '#cf2031',
          kicker: 'Objectif 3',
          title: "Réagir à l'urgence",
          body:
            "Freinage d'urgence et évitement d'obstacle : deux gestes qui peuvent sauver ta vie en circulation. Ils doivent devenir <strong>des réflexes</strong>.",
        },
        {
          icon: Route,
          accent: '#7c3aed',
          kicker: 'Objectif 4',
          title: 'Trajectoires précises',
          body:
            "Slalom, escargot, passage étroit : la précision du tracé conditionne ta sécurité en virage, en ville et sur route.",
        },
      ],
      cardsCols: 2,
    },

    {
      part: 'Partie 2 — Les 5 épreuves de maniabilité',
      title: 'Le détail de chaque épreuve',
      intro:
        "Une fois le <strong>contrôle visuel de l'équipement</strong> effectué (voir Partie 6), les épreuves de maniabilité s'enchaînent dans l'ordre fixé par l'examinateur. Voici ce qui t'attend et les <strong>clés de réussite</strong> de chaque exercice.",
      cards: [
        {
          icon: Bike,
          accent: '#64748b',
          kicker: '1. Déplacement',
          title: 'Sans moteur',
          body:
            "Tu pousses la moto, fais demi-tour, stationnes. <strong>Moteur coupé, vitesse enclenchée au stationnement</strong>. Erreurs : perte d'équilibre, mauvaise gestion du poids. <em>Clé : garde la moto contre toi, bras tendus, regard vers l'avant.</em>",
        },
        {
          icon: Timer,
          accent: '#0ea5e9',
          kicker: '2. Parcours lent',
          title: "Slalom, passage étroit, escargot",
          body:
            "Maîtriser la moto à <strong>très basse vitesse</strong>. Contient le slalom lent, le passage étroit et le fameux <strong>escargot</strong> (spirale resserrée). Interdiction de poser le pied. <em>Clé : regard loin + embrayage en zone de patinage + frein arrière dosé.</em>",
        },
        {
          icon: Zap,
          accent: '#f59e0b',
          kicker: '3. Parcours rapide',
          title: 'Slalom à vitesse élevée',
          body:
            "Accélérer et slalomer entre les cônes à vitesse soutenue. <strong>Regard sur la sortie</strong>, corps souple, pieds sur les repose-pieds. Erreurs : trajectoire trop large, regard fixé sur le cône.",
        },
        {
          icon: AlertTriangle,
          accent: '#cf2031',
          kicker: "4. Freinage d'urgence",
          title: "S'arrêter vite et droit",
          body:
            "<strong>Frein avant + arrière simultanément, moto droite</strong>, regard loin. Vitesse imposée (≈ 50 km/h). Erreurs éliminatoires : blocage de roue, freinage tardif, moto qui se couche.",
        },
        {
          icon: Target,
          accent: '#7c3aed',
          kicker: '5. Évitement',
          title: 'Contourner sans freiner',
          body:
            "Éviter un obstacle <strong>sans freiner</strong>, par <strong>contre-braquage</strong>. Vitesse imposée, regard vers la sortie, genoux serrés sur le réservoir. Erreur éliminatoire : freiner pendant l'évitement.",
        },
      ],
      cardsCols: 2,
    },

    {
      part: 'Partie 3 — Focus escargot',
      title: "L'escargot : l'épreuve qui fait trembler",
      intro:
        "C'est <strong>l'exercice le plus redouté</strong> du plateau. Pourtant, avec la bonne technique, il devient presque naturel. Voici les clés.",
      compareCards: [
        {
          accent: '#16a34a',
          kicker: 'Technique gagnante',
          title: '✓ Ce qu\'il faut faire',
          intro: 'Les 4 piliers de la réussite à l\'escargot :',
          points: [
            { ok: true, text: '<strong>Regard vers la sortie</strong> du virage (pas sur le cône qui arrive)' },
            { ok: true, text: '<strong>Moto inclinée</strong>, buste droit ou légèrement contre-incliné' },
            { ok: true, text: '<strong>Corps détendu</strong>, bras souples, genoux serrés' },
            { ok: true, text: '<strong>Frein arrière</strong> dosé en continu pour stabiliser' },
            { ok: true, text: 'Embrayage en <strong>zone de patinage</strong> (pas totalement lâché)' },
            { ok: true, text: 'Respiration régulière — ne pas bloquer' },
          ],
        },
        {
          accent: '#cf2031',
          kicker: 'Erreurs fréquentes',
          title: '✗ Ce qu\'il faut éviter',
          intro: "Les fautes qui font chuter ou sortir de l'exercice :",
          points: [
            { ok: false, text: "<strong>Fixer les cônes</strong> au lieu de regarder la sortie" },
            { ok: false, text: 'Couper les gaz brusquement (perte d\'équilibre immédiate)' },
            { ok: false, text: 'Poser le pied pour se rattraper (faute éliminatoire)' },
            { ok: false, text: 'Se crisper sur le guidon → la moto vibre et dévie' },
            { ok: false, text: "Oublier le frein arrière → l'avant de la moto devient instable" },
            { ok: false, text: 'Pencher le buste vers le bas (au lieu de rester droit)' },
          ],
          footer: "« Quand on regarde le cône, on touche le cône. »",
        },
      ],
    },

    {
      part: 'Partie 4 — Notation & fautes',
      title: 'Comment es-tu noté ?',
      intro:
        "L'examinateur attribue une lettre à chaque exercice : <strong>A, B ou C</strong>. Certaines fautes entraînent un <strong>échec immédiat</strong>.",
      normCards: [
        {
          code: 'A',
          color: '#16a34a',
          title: 'Parfait',
          body: "Exercice réalisé <strong>sans erreur significative</strong>, trajectoires respectées, gestes fluides. C'est l'objectif.",
        },
        {
          code: 'B',
          color: '#f59e0b',
          title: 'Acceptable',
          body: "Quelques imperfections (petite sortie de zone, hésitation), mais <strong>l'exercice reste validé</strong>. Tu peux décrocher le permis avec des B.",
        },
        {
          code: 'C',
          color: '#cf2031',
          title: 'Éliminatoire',
          body: "Faute majeure : <strong>échec de l'épreuve</strong>. Il faudra repasser le plateau.",
        },
      ],
      callouts: [
        {
          icon: AlertTriangle,
          accent: '#cf2031',
          body:
            "<strong>⚠️ Les fautes éliminatoires (C)</strong> : <strong>chute</strong> de la moto, <strong>sortie de parcours</strong>, <strong>refus d'obstacle</strong>, <strong>freinage pendant l'évitement</strong>, <strong>blocage de roue</strong> au freinage d'urgence, <strong>non-respect des consignes</strong> de l'examinateur.",
        },
      ],
    },

    {
      part: 'Partie 5 — Conseils moniteurs',
      title: 'Nos 4 conseils pour réussir',
      cards: [
        {
          icon: Eye,
          accent: '#1d4ed8',
          kicker: 'Conseil 1',
          title: 'Regarde loin',
          body:
            "<strong>Ta moto va où tu regardes.</strong> Dans les virages lents, vise la sortie, pas l'intérieur. En freinage, regarde l'horizon. En évitement, regarde <em>après</em> l'obstacle.",
        },
        {
          icon: Gauge,
          accent: '#16a34a',
          kicker: 'Conseil 2',
          title: 'Sois fluide',
          body:
            "Pas de gestes brusques. Accélérations progressives, embrayage dosé, frein arrière en continu sur le parcours lent. La fluidité rassure la moto (et l'examinateur).",
        },
        {
          icon: Zap,
          accent: '#7c3aed',
          kicker: 'Conseil 3',
          title: "Maîtrise l'embrayage",
          body:
            "Sur les exercices lents, tu dois <strong>sentir la zone de patinage</strong> : ni totalement lâché (la moto cale ou bondit), ni totalement tiré (tu perds la puissance).",
        },
        {
          icon: ShieldCheck,
          accent: '#cf2031',
          kicker: 'Conseil 4',
          title: 'Utilise le frein arrière',
          body:
            "Le frein arrière <strong>stabilise la moto</strong> à basse vitesse (parcours lent, escargot, passage étroit). Laisse-le dosé en permanence, sans bloquer.",
        },
      ],
      cardsCols: 2,
    },

    {
      part: 'Partie 6 — Avant le départ',
      title: "Les vérifications du début d'épreuve",
      intro:
        "L'épreuve commence <strong>avant même la première manœuvre</strong> : l'examinateur effectue un <strong>contrôle visuel de ton équipement</strong>. Pas de questions orales, pas d'inspection mécanique de la moto (c'est la responsabilité de la moto-école). Un équipement non conforme peut entraîner un <strong>refus de passage</strong>.",
      cards: [
        {
          icon: ShieldCheck,
          accent: '#16a34a',
          kicker: 'Contrôle unique',
          title: 'Ton équipement',
          body:
            "<strong>Casque homologué ECE 22-06</strong> correctement attaché, <strong>gants CE EN 13594</strong> certifiés moto, <strong>blouson à manches longues</strong>, <strong>pantalon long</strong>, <strong>bottes ou chaussures montantes</strong> couvrant la cheville. L'examinateur vérifie visuellement avant de te laisser partir.",
        },
        {
          icon: AlertTriangle,
          accent: '#cf2031',
          kicker: 'À savoir',
          title: 'Pas d\'interrogation orale',
          body:
            "Contrairement à une idée reçue, le plateau 2026 <strong>ne comporte pas de questions orales</strong> type « récite-moi les distances de sécurité », ni d'inspection mécanique de la moto. Les connaissances théoriques sont évaluées <strong>à l'ETM</strong> (code moto) et lors de <strong>l'épreuve en circulation</strong>.",
        },
      ],
      cardsCols: 2,
    },
  ],

  quiz: [
    {
      id: 'q1',
      question: "Quel est le principal risque de l'escargot ?",
      options: [
        'Rouler trop vite',
        "Fixer les cônes au lieu de regarder la sortie",
        'Trop freiner de l\'avant',
        "Regarder dans le rétroviseur",
      ],
      correctIndex: 1,
      explain:
        "Dès que tu fixes le cône, ta moto y va. Le regard doit être tourné vers la sortie du virage, loin devant. C'est la règle d'or de tout exercice lent.",
    },
    {
      id: 'q2',
      question:
        "Quel frein utilise-t-on principalement pour stabiliser la moto en parcours lent ?",
      options: [
        'Le frein avant',
        'Le frein arrière',
        'Le frein moteur uniquement',
        'Aucun, on laisse la moto rouler libre',
      ],
      correctIndex: 1,
      explain:
        "Le frein arrière dosé en continu stabilise l'arrière de la moto et évite les à-coups. Le frein avant, à basse vitesse, ferait plonger la moto et la ferait tomber.",
    },
    {
      id: 'q3',
      question:
        "Lors du freinage d'urgence, quelle est la faute éliminatoire la plus courante ?",
      options: [
        'Freiner trop tôt',
        'Regarder devant soi',
        'Le blocage de roue',
        'Utiliser les deux freins en même temps',
      ],
      correctIndex: 2,
      explain:
        "Un blocage de roue (avant ou arrière) est éliminatoire : il peut provoquer une chute immédiate. Il faut freiner fort mais progressif, moto droite, avec les deux freins simultanément.",
    },
    {
      id: 'q4',
      question: "Pendant l'évitement, que ne faut-il jamais faire ?",
      options: [
        'Regarder vers la sortie',
        'Freiner',
        'Serrer les genoux sur le réservoir',
        'Contre-braquer',
      ],
      correctIndex: 1,
      explain:
        "Freiner pendant l'évitement est une faute éliminatoire. On évite par contre-braquage à vitesse constante, puis on freine une fois la moto redressée.",
    },
    {
      id: 'q5',
      question:
        "Que contrôle l'examinateur AVANT la première manœuvre du plateau ?",
      options: [
        'Il te pose 3 questions orales tirées au hasard',
        "Uniquement ton équipement (casque, gants, vêtements)",
        "L'état mécanique complet de la moto",
        "Rien, il attend que tu commences l'escargot",
      ],
      correctIndex: 1,
      explain:
        "L'examinateur effectue un contrôle VISUEL de ton équipement (casque ECE 22-06, gants CE, blouson, pantalon long, chaussures montantes). Il ne fait pas d'inspection mécanique de la moto (c'est la responsabilité de la moto-école) et ne pose pas de questions orales — ces connaissances sont évaluées à l'ETM.",
    },
  ],

  ctaFinal: {
    kicker: 'Pour aller plus loin',
    title: 'Prêt pour la circulation ?',
    body:
      "Une fois le plateau validé, direction l'épreuve de circulation. Découvre les autres guides ECSR pour bien te préparer.",
    primary: {
      label: 'Voir les parcours A1 / A2',
      to: '/parcours',
    },
    secondary: {
      label: 'Lire le guide casque',
      to: '/magazine/bien-choisir-casque-moto',
    },
  },

  // Override du mapping ETM (Option C hybride) — ce guide pédagogique porte
  // sur le pilotage donc on le connecte prioritairement au thème M
  // (Mécanique et équipements).
  etm: {
    primary: 'M',
    secondary: ['L', 'S'],
    label: 'Mécanique & maîtrise du véhicule',
    cta: 'Réviser le thème « Mécanique »',
  },
};
